/**
 * Your year, one square a day.
 *
 * Colour reads completion: empty means nothing logged, full means every
 * scheduled activity ticked. Each square is a real button — tap or tab to it
 * and the diary jumps to that day.
 */
import { memo } from "react";
import { heatmapWeeks, formatDay, WEEKDAY_LABELS, type DailyBook } from "@/lib/dailyDiary";

function tone(ratio: number, total: number, future: boolean): string {
  if (future) return "bg-white/[0.02] border-white/5";
  if (total === 0 && ratio === 0) return "bg-white/[0.03] border-white/5";
  if (ratio === 0) return "bg-white/[0.05] border-white/10";
  if (ratio < 0.4) return "bg-primary/20 border-primary/25";
  if (ratio < 0.7) return "bg-primary/40 border-primary/35";
  if (ratio < 1) return "bg-primary/60 border-primary/50";
  return "bg-primary border-primary";
}

interface Props {
  book: DailyBook;
  selected: string;
  onSelect: (date: string) => void;
  weeks?: number;
}

function DiaryHeatmap({ book, selected, onSelect, weeks = 18 }: Props) {
  const grid = heatmapWeeks(book, weeks);

  return (
    <div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        <div className="flex flex-col gap-1 pr-1 shrink-0">
          {WEEKDAY_LABELS.map((label, i) => (
            <span key={label} className="h-4 text-[9px] leading-4 text-muted-foreground">
              {i % 2 === 1 ? label[0] : ""}
            </span>
          ))}
        </div>
        {grid.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-1 shrink-0">
            {week.map((cell) => {
              const label = `${formatDay(cell.date)} — ${cell.done} of ${cell.total || cell.done} done${cell.hasNote ? ", has a diary entry" : ""}`;
              return (
                <button
                  key={cell.date}
                  type="button"
                  title={label}
                  aria-label={label}
                  aria-current={cell.date === selected ? "date" : undefined}
                  onClick={() => onSelect(cell.date)}
                  className={`h-4 w-4 rounded-[4px] border transition hover:scale-125 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${tone(cell.ratio, cell.total, cell.future)} ${
                    cell.date === selected ? "ring-2 ring-offset-2 ring-offset-background ring-primary" : ""
                  } ${cell.hasNote ? "shadow-[inset_0_0_0_1px_rgba(255,255,255,0.35)]" : ""}`}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
        <span>Quieter</span>
        <span className="h-3 w-3 rounded-[3px] border border-white/10 bg-white/[0.05]" />
        <span className="h-3 w-3 rounded-[3px] border border-primary/25 bg-primary/20" />
        <span className="h-3 w-3 rounded-[3px] border border-primary/35 bg-primary/40" />
        <span className="h-3 w-3 rounded-[3px] border border-primary/50 bg-primary/60" />
        <span className="h-3 w-3 rounded-[3px] border border-primary bg-primary" />
        <span>Full day</span>
      </div>
    </div>
  );
}

export default memo(DiaryHeatmap);
